import axios from 'axios';
import Cookies from 'js-cookie';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { endpoints } from '@/lib/axios/axios-global';
import { getResolvedApiBaseUrl } from '@/core/global-data/constants.data';
import { useAppSelector } from '@/core/global-store';

export type AppBranding = {
    company_name: string;
    company_name_en: string | null;
    support_email: string | null;
    logo_url: string | null;
};

type AppBrandingContextValue = {
    branding: AppBranding | null;
    loaded: boolean;
    refetch: () => Promise<void>;
};

const AppBrandingContext = createContext<AppBrandingContextValue | null>(null);

function hasAuthToken(): boolean {
    if (Cookies.get('accessToken')) return true;
    const header = axios.defaults.headers.common.Authorization;
    return typeof header === 'string' && header.length > 0;
}

export function AppBrandingProvider({ children }: { children: ReactNode }) {
    const [branding, setBranding] = useState<AppBranding | null>(null);
    const [loaded, setLoaded] = useState(false);
    const authToken = useAppSelector((s) => s.auth.token);

    const refetch = useCallback(async () => {
        const base = getResolvedApiBaseUrl();
        if (!base) {
            setBranding(null);
            setLoaded(true);
            return;
        }
        try {
            // Authenticated users must hit /app-settings (tenant-scoped).
            // Public endpoint only returns real data when a Sanctum token is present.
            const path = hasAuthToken() ? endpoints.appSettings.show : endpoints.appSettings.public;
            const res = await axios.get<{ data: AppBranding }>(`${base}${path}`);
            setBranding(res.data?.data ?? null);
        } catch {
            setBranding(null);
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        void refetch();
    }, [refetch, authToken]);

    const value = useMemo(() => ({ branding, loaded, refetch }), [branding, loaded, refetch]);

    return <AppBrandingContext.Provider value={value}>{children}</AppBrandingContext.Provider>;
}

export function useAppBranding(): AppBrandingContextValue {
    const ctx = useContext(AppBrandingContext);
    if (!ctx) {
        throw new Error('useAppBranding must be used within AppBrandingProvider');
    }
    return ctx;
}
